/**
 * Apply a name search over the loaded pickup locations.
 *
 * @remarks
 * Shared by the search box (Enter / search-icon click) and the on-load URL
 * restore, so both behave identically. When the query matches pickup locations
 * by name (e.g. "Lowe's" — which can match many), the list is restricted to
 * every match, sorted by proximity to the user's location (IP) or the first
 * match, the map pans to the nearest match keeping the current zoom, and no
 * single detail panel is opened.
 *
 * Returns true when a name search was applied, false when nothing matched by
 * name — letting the caller fall back to geocoding the query as a place
 * (locality / zip / address), which is the right behavior for "Saratoga
 * Springs" but wrong for "Lowe's".
 */

import { locationStore } from "@/features/locations/stores/location-store";
import { mapViewStore } from "@/features/map/stores/map-view-store";
import { matchLocationsByName } from "@/features/locations/utils/location-name-search";
import { calculateDistance } from "@/features/locations/stores/helpers";
import { getCachedIpLocation } from "@/services/ip-geolocation";
import { urlState } from "@/services/url-state";
import type { Location } from "@/types/location";

export function applyNameSearch(query: string): boolean {
  const trimmed = query.trim();
  if (!trimmed) {
    return false;
  }

  const allLocations = locationStore.getState().getAllLocations();
  // No limit: a host-name search should surface every matching location,
  // not just the handful shown in the dropdown.
  const matches = matchLocationsByName(allLocations, trimmed, Infinity);
  if (matches.length === 0) {
    return false;
  }

  // Proximity origin: the user's location (IP) when known, otherwise the first
  // match. Used to sort the results and to pick the nearest match to pan to.
  const cachedLocation = getCachedIpLocation();
  const origin = cachedLocation
    ? { lat: cachedLocation.lat, lng: cachedLocation.lng }
    : { lat: matches[0].lat, lng: matches[0].lng };

  // Nearest match to the origin — the one the map pans to. matches is non-empty
  // (we returned early above), so seed with the first and scan for a closer one.
  const distanceFromOrigin = (location: Location) =>
    calculateDistance(origin.lat, origin.lng, location.lat, location.lng);
  let nearest = matches[0];
  let nearestDistance = distanceFromOrigin(nearest);
  for (const location of matches) {
    const distance = distanceFromOrigin(location);
    if (distance < nearestDistance) {
      nearest = location;
      nearestDistance = distance;
    }
  }

  // A multi-result search opens no single detail panel.
  locationStore.getState().selectLocation(null);
  urlState.clearParams(["loc", "slot"]);

  // Restrict the list to the matches; sort by proximity to the origin.
  const matchIds = new Set(matches.map((location) => location.id));
  locationStore
    .getState()
    .setNameSearch(matchIds, { ...origin, radiusMiles: 50 });

  // Persist the typed query (not a geocoded label, so a refresh re-runs the
  // same name search) and pan to the nearest match. Omitting zoom keeps the
  // current zoom level (see map-canvas centerOnCoordinates).
  locationStore.getState().updateFilters({ searchQuery: trimmed });
  mapViewStore
    .getState()
    .setMapCenter({ lat: nearest.lat, lng: nearest.lng });

  return true;
}
